import {Component, Inject, LOCALE_ID, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Subject} from 'rxjs';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {
  Country,
  Customer,
} from '~core/models';
import {
  CustomerService,
  NavigationService,
  StorageService,
} from '~core/services';
import {isPastDateDirective} from '~shared/directives/form-validators/is-past-date.directive';

/**
 * This component is responsible for the advanced search of customers.
 */
@UntilDestroy()
@Component({
  selector: 'app-advanced-search',
  templateUrl: './advanced-search.component.html'
})
export class AdvancedSearchComponent implements OnDestroy, OnInit {
  public advancedSearchForm: UntypedFormGroup;
  public selectedClub: any;
  public customers: Array<Customer> = [];
  public countries: Array<Country> = [];
  public searchInput: any = new Subject<string>();
  private recentSearch: any;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private customerService: CustomerService,
    private storage: StorageService,
    public navigationService: NavigationService,
    public flTranslations: FormLabelTranslationService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    if (this.storage.checkIfNotEmpty(`${this.locale}-countries`)) {
      this.storage.castCurrentCountries.pipe(untilDestroyed(this))
        .subscribe(countries => this.countries = countries);
    }
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe(club => this.selectedClub = club);
    this.customerService.castCustomerSearchResults.pipe(untilDestroyed(this))
      .subscribe(customers => this.customers = customers);
    this.navigationService.setCurrentPage('customerSearchAdvanced');
  }

  public ngOnDestroy(): void {
  }

  public ngOnInit(): void {
    this.createForm();
    this.customerService.castLastSearchUpdated.pipe(untilDestroyed(this))
      .subscribe(search => {
        if (search) {
          this.recentSearch = search;
          this.getSearchResultsAndFillData();
        }
      });
  }

  /**
   * Creates a form for the advanced search
   */
  private createForm(): void {
    this.advancedSearchForm = this.formBuilder.group({
      externalNumber: ['', Validators.minLength(2)],
      invoiceNumber: ['', Validators.minLength(2)],
      membershipNumber: ['', Validators.minLength(2)],
      company: ['', Validators.minLength(2)],
      firstName: ['', Validators.minLength(2)],
      lastName: ['', Validators.minLength(2)],
      birthday: ['', [Validators.minLength(2), isPastDateDirective]],
      email: ['', Validators.minLength(2)],
      street: ['', Validators.minLength(2)],
      postalCode: ['', Validators.minLength(2)],
      city: ['', Validators.minLength(2)],
      country: ['', Validators.minLength(2)]
    });
  }

  /**
   * Post the search form
   */
  public onSubmit(): void {
    const form = this.advancedSearchForm;
    if (form.valid) {
      for (const prop in form.value) {
        if (!form.value[prop]) {
          delete form.value[prop];
        }
      }
      this.searchCustomer(form.value);
    }
  }

  /**
   * Begins the search process
   */
  public searchCustomer(value: any): void {
    this.customerService.setSearchQuery(value, 'advanced');
  }


  get atLeastOneField(): boolean {
    return Object.values(this.advancedSearchForm.value).some(el => el);
  }

  /**
   * Clears all the search values
   */
  public clearAdvancedSearchValues(): void {
    this.advancedSearchForm.reset();
    this.customerService.setCustomerSearchResults(null);
  }

  /**
   * Retrieves the search results from the service, and fills all the form fields
   */
  public getSearchResultsAndFillData(): void {
    this.customerService.setCustomerSearchResults(null);
    if (this.recentSearch !== null && this.recentSearch.basic === false) {
      setTimeout(() => {
        const searchEntries = Object.entries(this.recentSearch.values);
        searchEntries.forEach(el => {
          const key = el[0];
          const value = el[1];
          this.advancedSearchForm.controls[key].patchValue(value);
        });
        this.searchCustomer(this.recentSearch.values);
        this.customerService.setLastSearchResults(null);
      }, 200);

    }
  }
}
